
import { Suspense } from 'react';
import { useSearchParams } from 'react-router';
import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui';

interface ViewResponsePayload {
  responseId: string;
  prayerId: string;
  prayerBody: string;
  responseType: string;
  textBody: string | null;
  playbackUrl: string | null;
  downloadUrl: string | null;
  createdAt: string;
}

async function downloadFile(url: string, filename: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

function ViewResponseContent() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [data, setData] = useState<ViewResponsePayload | null>(null);
  const [loading, setLoading] = useState(!!token);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing link.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await api.get<ViewResponsePayload>(
        `/view-response?token=${encodeURIComponent(token)}`,
        { auth: false }
      );
      if (cancelled) return;
      if (res.data) {
        if (res.data.responseType !== 'video' && res.data.responseType !== 'audio') {
          setError('This link may have expired or is invalid.');
        } else if (!res.data.playbackUrl) {
          setError('Unable to load response.');
        } else {
          setData(res.data);
        }
      } else {
        setError(res.error || 'Invalid or expired link.');
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleDownload = useCallback(async () => {
    if (!data?.downloadUrl) return;
    setDownloading(true);
    try {
      const ext = data.responseType === 'audio' ? 'webm' : 'mp4';
      await downloadFile(data.downloadUrl, `prayer-response.${ext}`);
    } catch {
      setError('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
        <div className="max-w-md text-center p-6 rounded-lg bg-white dark:bg-slate-800 shadow-sm">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            Unable to load response
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {error || 'This link may have expired or is invalid.'}
          </p>
        </div>
      </div>
    );
  }

  const isVideo = data.responseType === 'video';

  return (
    <div className="min-h-[100dvh] bg-black flex flex-col items-center justify-center p-4">
      <div
        className={`relative w-full mx-auto ${isVideo ? 'sm:max-w-xl' : 'max-w-md'}`}
        style={isVideo ? { aspectRatio: '9 / 16', maxHeight: '100dvh' } : undefined}
      >
        {isVideo ? (
          <video
            autoPlay
            controls
            className="w-full h-full object-cover rounded-lg"
            playsInline
            preload="auto"
          >
            <source src={data.playbackUrl!} type="video/mp4" />
          </video>
        ) : (
          <div className="w-full rounded-lg bg-slate-900 p-6 space-y-4">
            <p className="text-white text-center text-sm">Prayer response (audio)</p>
            <audio controls className="w-full" src={data.playbackUrl!} />
          </div>
        )}
      </div>
      {data.downloadUrl && (
        <Button
          variant="secondary"
          className="mt-4"
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? 'Downloading...' : 'Download'}
        </Button>
      )}
    </div>
  );
}

export default function ViewResponsePage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      }
    >
      <ViewResponseContent />
    </Suspense>
  );
}
